import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import {
  ProspectionService,
  ProspectionStats,
  RepartitionItem,
} from '../../services/prospection.service';

interface EtapeEntonnoir {
  libelle: string;
  valeur: number;
  taux?: number;
  suffixeTaux?: string;
  cible?: string;
  alerte?: boolean;
}

@Component({
  selector: 'app-prospection-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CardModule,
    MessageModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    SelectButtonModule,
    TableModule,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './prospection-dashboard.component.html',
  styleUrls: ['./prospection-dashboard.component.scss'],
})
export class ProspectionDashboardComponent implements OnInit {
  stats?: ProspectionStats;
  loading = false;
  erreur: string | null = null;

  periode = 30;
  optionsPeriode = [
    { label: '7 j', value: 7 },
    { label: '30 j', value: 30 },
    { label: '90 j', value: 90 },
  ];

  /** Jours restants avant la date butoir de dénonciation de la prévoyance collective. */
  joursAvantEcheance = 0;

  constructor(private prospectionService: ProspectionService) {}

  ngOnInit(): void {
    this.calculerEcheance();
    this.charger();
  }

  /**
   * Un contrat de prévoyance collective à échéance au 31/12 doit être dénoncé
   * 2 mois avant, soit au plus tard le 31 octobre. C'est la contrainte qui
   * cadence tout le trimestre : on l'affiche en permanence.
   */
  private calculerEcheance(): void {
    const aujourdhui = new Date();
    let butoir = new Date(aujourdhui.getFullYear(), 9, 31); // 31 octobre
    if (aujourdhui > butoir) {
      butoir = new Date(aujourdhui.getFullYear() + 1, 9, 31);
    }
    const msParJour = 1000 * 60 * 60 * 24;
    this.joursAvantEcheance = Math.ceil(
      (butoir.getTime() - aujourdhui.getTime()) / msParJour
    );
  }

  charger(): void {
    this.loading = true;
    this.erreur = null;
    this.prospectionService.getStats(this.periode).subscribe({
      next: (stats) => {
        this.stats = stats;
        this.loading = false;
      },
      error: (err) => {
        this.erreur =
          err?.error?.detail || 'Impossible de charger les statistiques de prospection.';
        this.loading = false;
      },
    });
  }

  changerPeriode(): void {
    this.charger();
  }

  /** Entonnoir de la période, avec les cibles du plan en regard de chaque étape. */
  get entonnoir(): EtapeEntonnoir[] {
    if (!this.stats) {
      return [];
    }
    const f = this.stats.funnel;
    const t = this.stats.taux;
    return [
      {
        libelle: 'Invitations LinkedIn',
        valeur: f.invitations_envoyees,
      },
      {
        libelle: 'Invitations acceptées',
        valeur: f.invitations_acceptees,
        taux: t.taux_acceptation_linkedin,
        suffixeTaux: 'des invitations',
        cible: '≥ 25 %',
        alerte: f.invitations_envoyees >= 30 && t.taux_acceptation_linkedin < 25,
      },
      {
        libelle: 'Emails envoyés',
        valeur: f.emails_envoyes,
      },
      {
        libelle: 'Réponses reçues',
        valeur: f.reponses,
        taux: t.taux_reponse_global,
        suffixeTaux: 'des touches',
        cible: '≥ 6 %',
        alerte: f.emails_envoyes >= 200 && t.taux_reponse_global < 6,
      },
      {
        libelle: 'Réponses positives',
        valeur: f.reponses_positives,
      },
      {
        libelle: 'Appels passés',
        valeur: f.appels_passes,
      },
      {
        libelle: 'Rendez-vous pris',
        valeur: f.rdv_pris,
        taux: t.taux_rdv,
        suffixeTaux: 'des touches',
        cible: '≥ 2,5 %',
        alerte: f.emails_envoyes + f.invitations_envoyees >= 500 && t.taux_rdv < 2.5,
      },
      {
        libelle: 'Convertis en contact CRM',
        valeur: f.convertis,
      },
    ];
  }

  pourcentageQuota(consomme: number, plafond: number): number {
    if (!plafond) {
      return 0;
    }
    return Math.min(100, Math.round((consomme / plafond) * 100));
  }

  severiteQuota(consomme: number, plafond: number): string {
    const pourcentage = this.pourcentageQuota(consomme, plafond);
    if (pourcentage >= 100) {
      return 'danger';
    }
    return pourcentage >= 80 ? 'warning' : 'success';
  }

  severiteBounce(taux: number): 'success' | 'warn' | 'danger' {
    if (taux > 3) {
      return 'danger';
    }
    return taux > 1.5 ? 'warn' : 'success';
  }

  libelleProduit(cle: string): string {
    const libelles: { [cle: string]: string } = {
      SANTE_COLLECTIVE: 'Santé collective',
      PREVOYANCE_COLLECTIVE: 'Prévoyance collective',
      SANTE_PRO_TNS: 'Santé / prévoyance TNS',
      EMPRUNTEUR: 'Assurance emprunteur',
    };
    return libelles[cle] || cle;
  }

  libelleStatut(cle: string): string {
    const libelles: { [cle: string]: string } = {
      A_QUALIFIER: 'À qualifier',
      A_CONTACTER: 'À contacter',
      EN_SEQUENCE: 'En séquence',
      REPONDU: 'A répondu',
      RDV: 'Rendez-vous',
      CONVERTI: 'Converti',
      NON_INTERESSE: 'Non intéressé',
      EXCLU: 'Exclu',
    };
    return libelles[cle] || cle;
  }

  totalRepartition(items: RepartitionItem[]): number {
    return (items || []).reduce((somme, item) => somme + item.total, 0);
  }

  partRepartition(item: RepartitionItem, items: RepartitionItem[]): number {
    const total = this.totalRepartition(items);
    return total ? Math.round((item.total / total) * 100) : 0;
  }
}
